import * as THREE from 'three';
import { World } from "../../world/World";

export class FiringEffect {
    world: World;
    fireMesh: THREE.Mesh;
    fireMaterial: THREE.ShaderMaterial;
    fireGeometry: THREE.ConeGeometry;

    constructor(world: World) {
        this.world = world;

        // Create a geometry for the fire effect with a cone
        this.fireGeometry = new THREE.ConeGeometry(0.2, 0.3, 8); // Base and height are initially defined

        // Create a shader material to simulate fire
        this.fireMaterial = new THREE.ShaderMaterial({
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                varying vec2 vUv;
                uniform float time;
                void main() {
                    // Randomize color based on UV and time
                    float intensity = 1.0 - length(vUv - 0.5) * 2.0;
                    float flicker = 0.5 + 0.5 * sin(time * 10.0);
                    vec3 color = vec3(1.0, 0.5, 0.2) * (intensity * flicker);
                    gl_FragColor = vec4(color, 1.0);
                }
            `,
            uniforms: {
                time: { value: 0 }
            },
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });

        // Create the mesh for the fire effect
        this.fireMesh = new THREE.Mesh(this.fireGeometry, this.fireMaterial);
        this.fireMesh.visible = false; // Initially hidden
        this.world.graphicsWorld.add(this.fireMesh); // Add the fire mesh to the scene
    }

    // Trigger the fire effect at the gun's muzzle
    triggerFiringFire(characterHand: THREE.Object3D) {
        // Get the world position of the hand (or gun muzzle)
        const handWorldPosition = new THREE.Vector3();
        characterHand.getWorldPosition(handWorldPosition);

        // Get the forward direction (facing direction of the gun)
        const handForward = new THREE.Vector3();
        characterHand.getWorldDirection(handForward);

        // Move the fire mesh slightly forward from the gun's position
        const forwardOffset = handForward.multiplyScalar(-0.3);
        handWorldPosition.add(forwardOffset);

        // Apply random scale and rotation
        const randomScale = 0.5 + Math.random() * 0.5; // Scale between 0.5 and 1.0
        const randomRotation = Math.random() * Math.PI * 2; // Random rotation angle

        // Set the fire mesh position, scale, and rotation
        this.fireMesh.position.copy(handWorldPosition);
        this.fireMesh.scale.set(randomScale, randomScale, randomScale);
        this.fireMesh.rotation.set(0, randomRotation, 0); // Rotate around Y-axis
        this.fireMesh.lookAt(this.world.camera.position); // Billboarding effect
        this.fireMesh.visible = true; // Show the fire effect

        // Animate the fire effect with flickering and random attributes
        this.animateFire();

        // Hide the fire after a short duration
        setTimeout(() => {
            this.fireMesh.visible = false;
        }, 50); // Effect lasts 50 milliseconds
    }

    // Animate the fire effect with randomness
    private animateFire() {
        const flickerDuration = 50; // Duration of flickering in milliseconds

        // Set a random time offset for the flicker effect
        const randomTimeOffset = Math.random() * 1000;
        let startTime = Date.now();

        const updateFire = () => {
            const elapsed = (Date.now() - startTime + randomTimeOffset) / flickerDuration;
            if (elapsed > 1) return;

            // Update shader uniform for flicker effect
            this.fireMaterial.uniforms['time'].value = elapsed * Math.PI * 2;

            requestAnimationFrame(updateFire);
        };

        updateFire();
    }
}
